import { t as tr } from "../../i18n";
import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject, type SVGProps } from "react";
import { propertyLineX, rcCounterfortProfile, type Pt, type WallInput, type WallResult } from "@tasduvar/engine";
import { backfillSurface, dimensionLines, fitScene, pathOf, toSvg, type Box, type Transform } from "./geometry";
import { Dimensions } from "./layers/Dimensions";
import { ActiveDimension } from "./ActiveDimension";

export interface Layers { dims: boolean; forces: boolean; pressure: boolean; base: boolean; joints: boolean; water: boolean; slope: boolean; rebar: boolean }
export const DEFAULT_LAYERS: Layers = { dims: true, forces: true, pressure: false, base: true, joints: true, water: true, slope: false, rebar: false };
const LAYER_LABELS: Record<keyof Layers, string> = { dims: "Ölçüler", forces: "Kuvvetler", pressure: "Basınç diyagramı", base: "Taban gerilmesi", joints: "Kesit kotları", water: "Su", slope: "Toptan göçme", rebar: "Donatı" };


/** SVG tanımları (ok ucu, tarama, taş deseni) — ekran ve rapor çizimi ortak. */
export function SvgDefs(): JSX.Element {
  return (
    <defs>
      <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#6B7280" /></marker>
      <marker id="arrowF" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#6B7280" /></marker>
      <pattern id="hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="6" stroke="#9CA3AF" strokeWidth="1" /></pattern>
      <pattern id="stone" width="14" height="10" patternUnits="userSpaceOnUse"><rect width="14" height="10" fill="#E5E7EB" /><path d="M0 5 H14 M7 0 V5 M0 5 V10 M14 5 V10" stroke="#9CA3AF" strokeWidth="0.8" fill="none" /></pattern>
      <pattern id="concrete" width="12" height="12" patternUnits="userSpaceOnUse"><rect width="12" height="12" fill="#E5E7EB" /><circle cx="3" cy="4" r="0.9" fill="#9CA3AF" /><circle cx="9" cy="9" r="0.7" fill="#9CA3AF" /><path d="M6 1 l1.5 1.2 -1 1.2 z" fill="#B5B9C2" /></pattern>
    </defs>
  );
}

export interface SceneProps { t: Transform; box: Box; surf: Pt[]; farX: number; wall: WallInput; result: WallResult; showWater: boolean }

/** Zemin, dolgu, kama, sanal düzlem, duvar ve su çizgileri (katman bileşenleri ve ölçüler hariç). */
export function Scene({ t, box, surf, farX, wall, result, showWater }: SceneProps): JSX.Element {
  const g = result.geometry;
  const Df = wall.geometry.embedmentDepth;
  const groundFront = toSvg(t, { x: box.minX, y: Df });
  const heelTop = toSvg(t, surf[0]!);
  const backfillPath = [surf[0]!, ...surf.slice(1), { x: farX, y: box.minY }, { x: g.heel.x, y: box.minY }, g.heel];
  // Payanda kesit düzleminin arkasında kalır → kesikli çizgiyle gösterilir
  const cf = wall.geometry.mode === "rc-cantilever" ? rcCounterfortProfile(wall.geometry) : null;
  return (
    <>
      <path data-part="backfill" d={pathOf(t, backfillPath)} fill="url(#hatch)" stroke="#9CA3AF" strokeWidth={1} />
      <path data-part="front-ground" d={pathOf(t, [{ x: box.minX, y: Df }, { x: g.toe.x, y: Df }, { x: g.toe.x, y: box.minY }, { x: box.minX, y: box.minY }])} fill="url(#hatch)" stroke="#9CA3AF" strokeWidth={1} />
      <line data-part="ground-line" x1={groundFront.x} y1={groundFront.y} x2={toSvg(t, g.toe).x} y2={groundFront.y} stroke="#4B5563" strokeWidth={1.5} />
      <polyline data-part="backfill-surface" points={surf.map((p) => { const s = toSvg(t, p); return `${s.x},${s.y}`; }).join(" ")} fill="none" stroke="#4B5563" strokeWidth={1.5} />
      {g.wedgeArea > 1e-6 ? <path data-part="wedge" d={pathOf(t, g.wedge)} fill="#D1D5DB" fillOpacity={0.5} stroke="#6B7280" strokeDasharray="3 2" /> : null}
      <line data-part="virtual-plane" x1={toSvg(t, g.heel).x} y1={toSvg(t, g.heel).y} x2={toSvg(t, g.virtualPlane.top).x} y2={toSvg(t, g.virtualPlane.top).y} stroke="#1F4E79" strokeDasharray="4 3" strokeWidth={1} />
      <path data-part="wall" d={pathOf(t, g.polygon)} fill={result.structureType === "masonry-gravity" ? "url(#stone)" : "url(#concrete)"} stroke="#1F2937" strokeWidth={1.8} />
      {g.key ? <path data-part="key" d={pathOf(t, g.key)} fill="url(#concrete)" stroke="#1F2937" strokeWidth={1.8} /> : null}
      {cf ? <path data-part="counterfort" d={pathOf(t, cf)} fill="#9CA3AF" fillOpacity={0.18} stroke="#1F2937" strokeWidth={1} strokeDasharray="5 3" /> : null}
      {showWater && wall.water.backLevel !== null ? (() => { const y = toSvg(t, { x: 0, y: wall.water.backLevel }).y; return <g data-part="water-back"><line x1={heelTop.x} y1={y} x2={toSvg(t, { x: farX, y: 0 }).x} y2={y} stroke="#2563EB" strokeWidth={1.2} strokeDasharray="6 3" /><text x={toSvg(t, { x: farX, y: 0 }).x - 4} y={y - 3} textAnchor="end" className="text-[10px] fill-blue-600 num">{tr("▽ YASS")}</text></g>; })() : null}
      <PropertyLineMark t={t} box={box} wall={wall} />
      {showWater && wall.water.frontLevel !== null ? (() => { const y = toSvg(t, { x: 0, y: wall.water.frontLevel }).y; return <line data-part="water-front" x1={groundFront.x} y1={y} x2={toSvg(t, g.toe).x} y2={y} stroke="#2563EB" strokeWidth={1.2} strokeDasharray="6 3" />; })() : null}
    </>
  );
}

/** Parsel sınırı: kesik-noktalı kırmızı düşey çizgi + etiket (Cem 2026-10-02) */
function PropertyLineMark({ t, box, wall }: { t: Transform; box: Box; wall: WallInput }): JSX.Element | null {
  const x = propertyLineX(wall.geometry, wall.propertyLine);
  if (x === null) return null;
  const a = toSvg(t, { x, y: box.maxY }), b = toSvg(t, { x, y: box.minY });
  // Yazı çizgi boyunca (düşey), dışarı bakan yanda, gömme kotu ile tepe arasının ortasında — ölçülerle çakışmasın
  const front = wall.propertyLine!.side === "front";
  const m = toSvg(t, { x, y: (wall.geometry.embedmentDepth + box.maxY) / 2 });
  const tx = m.x + (front ? -5 : 12);
  return (
    <g data-part="property-line">
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#C0262D" strokeWidth={1.4} strokeDasharray="10 3 2 3" />
      <text x={tx} y={m.y} transform={`rotate(-90 ${tx} ${m.y})`} textAnchor="middle" className="text-[10px] font-semibold" fill="#C0262D">{tr("PARSEL SINIRI")}</text>
    </g>
  );
}

export interface SectionDrawingProps {
  wall: WallInput; result: WallResult | null; error: string | null;
  layers: Layers; onLayers: (l: Layers) => void;
  width?: number; height?: number;
  children?: (t: Transform) => ReactNode;   // sonuç katmanları (kuvvet, basınç, derz) buradan girer
  onEditDimension?: (key: string, value: number) => void;   // varsa ölçü etiketleri tıklanıp düzenlenebilir
  polygonEditor?: (t: Transform) => ReactNode;               // çokgen modunda köşe sürükleme katmanı
  groundHandles?: (t: Transform, s: { box: Box; surf: Pt[] }) => ReactNode;   // Df / β sürükleme tutamaçları
  hideLayerBar?: boolean;                                    // katman seçici dışarıda çiziliyorsa
  extraPoints?: Pt[];                                        // sahneye sığdırılacak ek noktalar (toptan göçme dairesi)
  /** Sağda yüzen palet genişliği (px): sığdırırken duvar ve tutamaçları paletin solunda tutulur (dolgu altına uzayabilir) */
  reserveRight?: number;
  /** CAD model alanı (ModelSpace): pencere `viewBox` (null = sığdır), SVG olay/ref, overlay (crosshair, yakalama işareti) ve sahne dönüşümü bildirimi */
  viewBox?: { x: number; y: number; w: number; h: number } | null;
  svgRef?: RefObject<SVGSVGElement>;
  svgProps?: Omit<SVGProps<SVGSVGElement>, "ref" | "viewBox" | "width" | "height" | "className" | "role">;
  overlay?: (t: Transform, vb: { x: number; y: number; w: number; h: number }) => ReactNode;
  onScene?: (t: Transform, size: { width: number; height: number }) => void;
}
export function LayerChips({ layers, onLayers, only }: { layers: Layers; onLayers: (l: Layers) => void; /** yalnız bu katmanlar (3B görünümde kuvvetler) */ only?: (keyof Layers)[] }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label={tr("Çizim katmanları")}>
      {(only ?? (Object.keys(LAYER_LABELS) as (keyof Layers)[])).map((k) => (
        <label key={k} className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs cursor-pointer select-none transition-colors duration-150 ${layers[k] ? "border-ink/40 bg-ink-light text-ink" : "border-rule text-stone hover:text-ink"}`}>
          <input type="checkbox" className="sr-only" checked={layers[k]} onChange={(e) => onLayers({ ...layers, [k]: e.target.checked })} aria-label={tr(LAYER_LABELS[k])} />
          <span aria-hidden className={`inline-block w-1.5 h-1.5 rounded-full ${layers[k] ? "bg-ink" : "bg-rule"}`} />{tr(LAYER_LABELS[k])}
        </label>
      ))}
    </div>
  );
}

/** Kapsayıcının gerçek boyutunu izler; viewBox buna eşitlenir ki çizim alanın tamamını kullansın. jsdom'da ResizeObserver yoktur → verilen boyut. */
function useContainerSize(fallback: { width: number; height: number }): [RefObject<HTMLDivElement>, { width: number; height: number }] {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      const r = entry?.contentRect; if (!r || r.width < 50 || r.height < 50) return;
      setSize((s) => (Math.abs(s.width - r.width) < 1 && Math.abs(s.height - r.height) < 1 ? s : { width: Math.round(r.width), height: Math.round(r.height) }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

export function SectionDrawing({ wall, result, error, layers, onLayers, width: w0 = 720, height: h0 = 480, children, onEditDimension, polygonEditor, groundHandles, hideLayerBar, extraPoints, reserveRight = 0, viewBox, svgRef, svgProps, overlay, onScene }: SectionDrawingProps): JSX.Element {
  const [boxRef, { width, height }] = useContainerSize({ width: w0, height: h0 });
  const scene = useMemo(() => {
    if (!result) return null;
    const { box, t: t0 } = fitScene(result, wall, width, height, 24, extraPoints);
    let t = t0;
    // Sağda palet varsa yalnız duvar (ve topuk ucundaki ölçü/tutamaç payı) onun solunda kalsın: gerekirse küçült, sola yasla;
    // arkadaki dolgu paletin altına uzayabilir. Çok küçülecekse (yarıdan az) olduğu gibi bırak.
    if (reserveRight > 0) {
      const xWall = Math.max(...result.geometry.polygon.map((p) => p.x)), PAD = 64;
      const limit = width - reserveRight - PAD;
      if (xWall * t.scale + t.ox > limit) {
        const s = Math.min(t.scale, (limit - 24) / (xWall - box.minX));
        if (s >= 0.5 * t.scale) t = { ...t, scale: s, ox: 24 - box.minX * s, oy: (height + (box.maxY - box.minY) * s) / 2 + box.minY * s };
      }
    }
    // Ayrılan şeritte de dolgu devam etsin (palet kapanınca ya da yarı saydam kenarda boşluk görünmesin)
    const farX = Math.max(box.maxX, (width - t.ox) / t.scale);
    return { t, box, surf: backfillSurface(result, wall, farX), dims: dimensionLines(result, wall), farX };
  }, [result, wall, width, height, extraPoints, reserveRight]);
  useEffect(() => { if (scene && onScene) onScene(scene.t, { width, height }); }, [scene, width, height, onScene]);
  const vb = viewBox ?? { x: 0, y: 0, w: width, h: height };

  return (
    <div className="flex flex-col h-full">
      {hideLayerBar ? null : <div className="px-2 py-1.5 border-b border-rule"><LayerChips layers={layers} onLayers={onLayers} /></div>}
      {error ? <p role="alert" className="m-2 rounded-md border border-bad bg-bad/5 p-2 text-sm text-bad">{tr("Hesap yapılamadı:")} {error}</p> : null}
      <div ref={boxRef} className="flex-1 min-h-[320px] relative">
      <svg ref={svgRef} role="img" aria-label={tr("Duvar kesiti")} viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} width={width} height={height} preserveAspectRatio="xMidYMid meet" className={`absolute inset-0 ${error ? "ring-2 ring-bad rounded-md" : ""}`} {...svgProps}>
        <SvgDefs />
        {scene && result ? (<>
          <Scene t={scene.t} box={scene.box} surf={scene.surf} farX={scene.farX} wall={wall} result={result} showWater={layers.water} />
          {layers.dims ? <Dimensions t={scene.t} dims={scene.dims} showLabels={!onEditDimension} /> : null}
          {children ? children(scene.t) : null}
          {polygonEditor ? polygonEditor(scene.t) : null}
          {/* Ölçü etiketleri düzenleyicinin üstünde: küçük ölçekte kenarın geniş tıklama çizgisi etiketi örtmesin */}
          {layers.dims && onEditDimension ? scene.dims.map((d) => <ActiveDimension key={d.key} t={scene.t} dim={d} onCommit={(v) => onEditDimension(d.key, v)} />) : null}
          {groundHandles ? groundHandles(scene.t, { box: scene.box, surf: scene.surf }) : null}
          {overlay ? overlay(scene.t, vb) : null}
        </>) : null}
      </svg>
      </div>
    </div>
  );
}
